import {
  Component,
  ViewChild,
  ElementRef,
  inject,
  signal,
  computed,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import * as echarts from 'echarts';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { IconFieldModule } from 'primeng/iconfield';
import { ChangeDetectorRef } from '@angular/core';
import { InputIconModule } from 'primeng/inputicon';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MenuItem } from 'primeng/api';
import { SkeletonModule } from 'primeng/skeleton';
import { FormArray, FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { Core } from '../../_core/core';
import { HomeS } from '../../_core/home';
import { Validators } from '@angular/forms';
import { Spinner } from '../../_core/spinner';
import { ReactiveFormsModule } from '@angular/forms';
import { CarouselModule } from 'primeng/carousel';
import { Metering, DailyChartFingerprint } from '../../_core/metering';
import {
  downloadTableFile,
  exportFilename,
  exportNumberCell,
  exportTextCell,
  ExportFormat,
  ExportTable,
} from '../../utils/table-file-export';
interface SupplyType {
  supplyTypeId: number;
  supplyName: string;
  readingPlannedDate?: string;
}

type ChartDrill =
  | { kind: 'frequency'; frequency: 'MONTH' | 'DAY'; inicio: Date; fin: Date }
  | { kind: 'hourly'; day: Date };

@Component({
  selector: 'app-daily-readings',
  imports: [
    CommonModule,
    TooltipModule,
    CarouselModule,
    SelectModule,
    DatePickerModule,
    ReactiveFormsModule,
    LucideAngularModule,
    TableModule,
    CardModule,
    IconFieldModule,
    InputIconModule,
    /* InputGroupAddonModule, */ InputTextModule,
    PopoverModule,
    ButtonModule,
    SplitButtonModule,
    SkeletonModule,
    FormsModule,
    MessageModule,
  ],

  templateUrl: './daily-readings.html',
  styleUrl: './daily-readings.scss',
})
export class DailyReadings implements OnInit, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  @ViewChild('chartContainer2') chartContainer2!: ElementRef;

  private $spinner = inject(Spinner);
  private $core = inject(Core);

  private cdr = inject(ChangeDetectorRef);
  private $home = inject(HomeS);
  private $readingsService = inject(Metering);
  private readonly platformId = inject(PLATFORM_ID);
  public isDownloading = signal(false);
  readonly exportMenuItems: MenuItem[] = [
    { label: 'CSV', icon: 'pi pi-file', command: () => void this.exportReadings('csv') },
    { label: 'Excel', icon: 'pi pi-file-excel', command: () => void this.exportReadings('xlsx') },
  ];
  /** Solo para la tabla «Listado de consumos y lecturas» (viewport < 768px). */
  public readonly isNarrowViewport = signal(false);
  private narrowViewportQuery: MediaQueryList | null = null;
  private narrowViewportListener: ((event: MediaQueryListEvent) => void) | null = null;

  public homeData = this.$home.homeData;
  public frequencyType = [
    { label: 'Diaria', value: 'DAY' },
    { label: 'Semanal', value: 'WEEK' },
    { label: 'Mensual', value: 'MONTH' },
    { label: 'Anual', value: 'YEAR' },
  ];
  public supplies = this.$readingsService.supplies;
  public meters = this.$readingsService.meters;
  readonly hasSingleSupply = computed(() => this.supplies().length === 1);
  readonly hasSingleMeter = computed(() => this.meters().length === 1);
  public dailyConsumptions = this.$readingsService.consumptionsDaily;
  public dailyConsumptionsUnits = this.$readingsService.consumptionUnitsDaily;
  public hasEstimatedConsumptionsDaily = this.$readingsService.hasEstimatedConsumptionsDaily;
  public dailyHourlyConsumptionsError = this.$readingsService.dailyHourlyConsumptionsError;
  public visibleDailyConsumptionsTable = computed(() =>
    this.dailyConsumptions().filter((row) => !this.isEmptyConsumptionRow(row)),
  );
  public first: number = 0;
  public rows: number = 10;
  readonly hourlyTableRows = 24;

  public tenantSelected: any;
  public maxDate: Date = new Date();
  public minDate: Date = new Date();

  public configForm!: FormGroup;
  get comparativas() {
    return this.configForm.get('comparativas') as FormArray;
  }
  public currentPage = signal<number>(0);

  public filteredMeters = signal<any[]>([]);

  public resumen = {
    avg: '0,00',
    maxV: '0,00',
    maxLabel: '---',
    minV: '0,00',
    minLabel: '---',
    total: '0,00',
    units: '',
  };
  public loading: boolean = false;
  public initialLoading = signal(true);
  public tableInitialized = signal(false);
  private initialTableLoad = true;
  public showDateRangeYearWarning = signal(false);

  constructor(private fb: FormBuilder) {
    this.initForms();
    this.setMaxMinDates();
    this.tenantSelected = this.$core.selectedBuilding();
  }
  async ngOnInit() {
    this.initNarrowViewportListener();

    if (!this.tenantSelected) {
      this.tenantSelected = await this.$core.getTenantFromLocal();
    }

    if (!this.isDashboardPrefetched()) {
      await this.$home.fetchHomeData();
    }

    if (!this.areSuppliesPrefetched()) {
      await this.$readingsService.getSupplies(this.tenantSelected!.tenantId);
    }

    const currentSupplies = this.supplies();
    let cacheHit = false;

    if (currentSupplies && currentSupplies.length > 0) {
      const firstSupply = currentSupplies[0];
      this.configForm.get('supplyType')?.setValue(firstSupply, { emitEvent: false });

      if (!this.$readingsService.hasMetersForSupply(this.getSupplyTypeId(firstSupply))) {
        await this.$readingsService.getMeters(
          this.tenantSelected!.tenantId,
          this.getSupplyTypeId(firstSupply),
        );
      }

      const bootstrapFingerprint = this.buildDailyFingerprint(firstSupply);
      cacheHit = this.$readingsService.matchesDailyChartFingerprint(bootstrapFingerprint);

      if (!cacheHit) {
        this.beginInitialPageLoad();
      }

      if (cacheHit) {
        this.initialLoading.set(false);
        this.initialTableLoad = true;
        await this.hydrateDailyBootstrap();
        this.markTableInitializedIfReady();
        await this.createChart();
      } else {
        await this.onSupplyTypeChange({ value: firstSupply }, true);
      }
    }

    if (!cacheHit && currentSupplies?.length) {
      this.endInitialPageLoad();
    } else if (cacheHit) {
      this.finishBootstrapTableState();
      this.initialLoading.set(false);
    } else {
      this.initialLoading.set(false);
    }
  }

  private markTableInitializedIfReady(): void {
    this.tableInitialized.set(true);
  }

  private finishBootstrapTableState(): void {
    this.tableInitialized.set(true);
  }

  private beginInitialPageLoad(): void {
    this.$spinner.show();
    this.loading = true;
    this.initialLoading.set(true);
    this.first = 0;
    this.initialTableLoad = true;
  }

  private endInitialPageLoad(): void {
    this.initialLoading.set(false);
    this.loading = false;
    this.$spinner.hide();
    this.finishBootstrapTableState();
  }

  private endUserLoading(): void {
    this.loading = false;
    this.$spinner.hide();
  }

  private isDashboardPrefetched(): boolean {
    return this.homeData() !== null;
  }

  private areSuppliesPrefetched(): boolean {
    return (
      this.$readingsService.isForTenant(this.tenantSelected!.tenantId) && this.supplies().length > 0
    );
  }

  get isHourlyView(): boolean {
    return this.configForm?.controls['formatType']?.value === '1';
  }

  get tableColumnCount(): number {
    return 4;
  }

  private getSelectedDateRange(): { dateStart: string; dateEnd: string } | null {
    if (!this.configForm) {
      return null;
    }

    if (this.isHourlyView) {
      const inicio = this.comparativas.at(0).value?.inicio;
      if (!(inicio instanceof Date)) {
        return null;
      }
      const day = this.formatDate(inicio);
      return { dateStart: day, dateEnd: day };
    }

    const { inicio, fin } = this.comparativas.at(0).value;
    if (!(inicio instanceof Date) || !(fin instanceof Date)) {
      return null;
    }
    return { dateStart: this.formatDate(inicio), dateEnd: this.formatDate(fin) };
  }

  private getBootstrapDateStrings(): { from: string; to: string } {
    const range = this.getSelectedDateRange();
    return { from: range?.dateStart ?? '', to: range?.dateEnd ?? '' };
  }

  private getDailyFrequency(): string {
    return this.configForm.controls['formatType'].value === '0'
      ? this.configForm.controls['frequencyType'].value
      : 'HOUR';
  }

  private getSupplyTypeId(supply: { supplyTypeId?: number; id?: number }): number {
    return supply.supplyTypeId ?? supply.id ?? 0;
  }

  private buildDailyFingerprint(
    supply: { supplyTypeId?: number; id?: number },
    meterId = this.meters()[0]?.meterId ?? 0,
  ): DailyChartFingerprint {
    const supplyTypeId = this.getSupplyTypeId(supply);
    const { from, to } = this.getBootstrapDateStrings();

    return {
      tenantId: this.tenantSelected!.tenantId,
      supplyTypeId,
      meterId,
      frequency: this.getDailyFrequency(),
      from,
      to,
      formatType: this.configForm.controls['formatType'].value,
    };
  }

  private hydrateDailyBootstrap(): void {
    const currentMeters = this.meters();
    if (!currentMeters.length) {
      return;
    }

    this.filteredMeters.set(currentMeters);
    const firstMeter = currentMeters[0];
    const meterControl = this.configForm.get('meter');
    meterControl?.enable();
    meterControl?.setValue(firstMeter, { emitEvent: false });
    this.markTableInitializedIfReady();
  }

  setMaxMinDates() {
    this.maxDate = new Date();
    this.minDate = new Date(this.maxDate);
    this.minDate.setFullYear(this.maxDate.getFullYear() - 5);
  }
  initForms() {
    const { from, to } = this.$readingsService.getDefaultDailyRange();
    const defaultOption = {
      label: 'Último mes',
      value: { startDate: from, endDate: to },
    };
    this.configForm = this.fb.group({
      supplyType: [null, Validators.required],
      meter: [{ value: null, disabled: true }, Validators.required],
      formatType: ['0', Validators.required],
      frequencyType: ['DAY', Validators.required],
      comparativas: this.fb.array([this.createDateGroup()]),
      predefineDateOption: [defaultOption],
    });
    this.applyRangeToComparativas(from, to);

    this.configForm.get('meter')?.valueChanges.subscribe((meter) => {
      if (meter) {
        const index = this.filteredMeters().findIndex((m) => m.meterId === meter.meterId);
        if (index !== -1 && index !== this.currentPage()) {
          this.currentPage.set(index);
        }
      }
    });
    this.configForm.get('supplyType')?.valueChanges.subscribe(() => {
      this.currentPage.set(0);
    });
  }

  async onSupplyTypeChange(event: any, isBootstrap = false) {
    this.first = 0;
    if (!isBootstrap) {
      this.$spinner.show();
      this.loading = true;
    }

    const supplyTypeId = this.getSupplyTypeId(event.value);
    const meterControl = this.configForm.get('meter');

    try {
      if (supplyTypeId) {
        await this.$readingsService.getMeters(this.tenantSelected!.tenantId, supplyTypeId);

        if (!isBootstrap) {
          this.$spinner.show();
          this.loading = true;
        }

        const currentMeters = this.meters();

        if (currentMeters && currentMeters.length > 0) {
          this.filteredMeters.set(currentMeters || []);
          const firstMeter = currentMeters[0];
          meterControl?.enable();
          meterControl?.setValue(firstMeter, { emitEvent: false });
          this.ensureHourlyViewAllowed();

          const range = this.getSelectedDateRange();
          if (!range) {
            return;
          }

          const fingerprint = this.buildDailyFingerprint(event.value, firstMeter.meterId);
          const fetchOptions = {
            manageSpinner: !isBootstrap,
            fingerprint,
          };

          this.$readingsService.clearDailyHourlyConsumptionsError();
          await this.$readingsService.fetchConsumptionsDailyHourly(
            this.tenantSelected!.tenantId,
            supplyTypeId,
            firstMeter.meterId,
            this.getDailyFrequency(),
            range.dateStart,
            range.dateEnd,
            fetchOptions,
          );
          this.markTableInitializedIfReady();
          await this.createChart();
        } else {
          meterControl?.setValue(null);
          meterControl?.disable();
        }
      } else {
        meterControl?.setValue(null);
        meterControl?.disable();
      }
    } finally {
      if (!isBootstrap) {
        this.endUserLoading();
      }
    }
  }
  private async reloadDailyData(): Promise<void> {
    if (this.configForm.invalid) {
      return;
    }

    const tenantSelected = this.tenantSelected || (await this.$core.getTenantFromLocal());
    if (!tenantSelected?.tenantId) {
      return;
    }

    const supply = this.configForm.get('supplyType')?.value;
    const meter = this.configForm.get('meter')?.value;
    const range = this.getSelectedDateRange();
    if (!supply || !meter || !range) {
      return;
    }

    this.first = 0;
    this.$spinner.show();
    this.loading = true;
    this.$readingsService.clearDailyHourlyConsumptionsError();
    const fingerprint = this.buildDailyFingerprint(supply, meter.meterId);

    try {
      await this.$readingsService.fetchConsumptionsDailyHourly(
        tenantSelected.tenantId,
        this.getSupplyTypeId(supply),
        meter.meterId,
        this.getDailyFrequency(),
        range.dateStart,
        range.dateEnd,
        { manageSpinner: false, fingerprint },
      );
      await this.createChart();
      this.tableInitialized.set(true);
    } catch (error) {
      console.error('Error al cargar consumos:', error);
    } finally {
      this.$spinner.hide();
      this.loading = false;
    }
  }

  async getConsumptionsDaily() {
    this.first = 0;
    this.initialTableLoad = false;
    this.tableInitialized.set(false);
    await this.reloadDailyData();
  }

  onConsumptionsPage(event: { first?: number; rows?: number }): void {
    this.first = event.first ?? 0;
    if (event.rows != null) {
      this.rows = event.rows;
    }
  }

  async onMeterChange(event: any) {
    if (event.value) {
      this.ensureHourlyViewAllowed();
      await this.getConsumptionsDaily();
    }
  }

  isHourlyReadingsAvailable(): boolean {
    const meter = this.configForm?.get('meter')?.value as { hasHourlyReadings?: boolean } | null;
    return meter?.hasHourlyReadings === true;
  }

  meterModuleNumber(meter: { serialNumber?: string; serialNumber1?: string } | null): string {
    return meter?.serialNumber || meter?.serialNumber1 || '';
  }

  selectView(value: '0' | '1'): void {
    if (value === '1' && !this.isHourlyReadingsAvailable()) {
      return;
    }
    if (this.configForm.controls['formatType'].value === value) {
      return;
    }
    this.configForm.controls['formatType'].setValue(value);
    if (value === '1') {
      this.collapseRangeToSelectedDay();
    }
    void this.onFormatTypeChange();
  }

  selectFrequency(value: string): void {
    if (this.configForm.controls['frequencyType'].value === value) {
      return;
    }
    this.configForm.controls['frequencyType'].setValue(value);
    void this.onFrequencyTypeChange({ value });
  }

  get quickDateOptions(): { label: string; startDate: string; endDate: string }[] {
    const endDate = this.formatDate(new Date());
    const option = (label: string, startDate: string) => ({ label, startDate, endDate });
    switch (this.configForm?.controls['frequencyType']?.value) {
      case 'WEEK':
        return [
          option('Último mes', this.getDateMonthsAgo(1)),
          option('Últimos 2 meses', this.getDateMonthsAgo(2)),
          option('Últimos 6 meses', this.getDateMonthsAgo(6)),
        ];
      case 'MONTH':
        return [
          option('Últimos 6 meses', this.getDateMonthsAgo(6)),
          option('Último año', this.getDateYearsAgo(1)),
          option('Últimos 2 años', this.getDateYearsAgo(2)),
        ];
      case 'YEAR':
        return [
          option('Último año', this.getDateYearsAgo(1)),
          option('Últimos 2 años', this.getDateYearsAgo(2)),
        ];
      default:
        return [
          option('Última semana', this.getDateDaysAgo(7)),
          option('Últimos 15 días', this.getDateDaysAgo(15)),
          option('Último mes', this.getDateDaysAgo(30)),
          option('Últimos 2 meses', this.getDateMonthsAgo(2)),
        ];
    }
  }

  isQuickDateActive(option: { startDate: string; endDate: string }): boolean {
    const { inicio, fin } = this.comparativas.at(0).value ?? {};
    if (!(inicio instanceof Date) || !(fin instanceof Date)) {
      return false;
    }
    return this.formatDate(inicio) === option.startDate && this.formatDate(fin) === option.endDate;
  }

  applyQuickDate(option: { label: string; startDate: string; endDate: string }): void {
    this.clearDateRangeYearWarning();
    this.configForm.controls['predefineDateOption'].setValue({
      label: option.label,
      value: { startDate: option.startDate, endDate: option.endDate },
    });
    this.applyRangeToComparativas(option.startDate, option.endDate);
    void this.applyConfig();
  }

  canShiftHourlyDay(delta: number): boolean {
    const inicio = this.comparativas.at(0)?.get('inicio')?.value;
    if (!(inicio instanceof Date)) {
      return false;
    }
    const next = this.toDateOnly(inicio);
    next.setDate(next.getDate() + delta);
    return next >= this.toDateOnly(this.minDate) && next <= this.toDateOnly(this.maxDate);
  }

  shiftHourlyDay(delta: number): void {
    if (!this.canShiftHourlyDay(delta)) {
      return;
    }
    const inicio = this.comparativas.at(0).get('inicio')?.value as Date;
    const next = this.toDateOnly(inicio);
    next.setDate(next.getDate() + delta);
    this.comparativas.at(0).patchValue({ inicio: next, fin: next }, { emitEvent: false });
    void this.applyConfig();
  }

  onHourlyDaySelect(): void {
    const inicio = this.comparativas.at(0).get('inicio')?.value;
    if (!(inicio instanceof Date)) {
      return;
    }
    const day = this.toDateOnly(inicio);
    this.comparativas.at(0).patchValue({ inicio: day, fin: day }, { emitEvent: false });
    void this.applyConfig();
  }

  onDateChange(index: number, field: 'inicio' | 'fin') {
    const group = this.comparativas.at(index);
    let { inicio, fin } = group.value;
    if (!(inicio instanceof Date) || !(fin instanceof Date)) return;

    const MAX_YEARS = 2;
    inicio = this.toDateOnly(inicio);
    fin = this.toDateOnly(fin);
    const maxAllowed = this.toDateOnly(this.maxDate);
    const minAllowed = this.toDateOnly(this.minDate);

    if (field === 'inicio') {
      if (inicio > fin) {
        fin = new Date(inicio);
      }
      const maxFin = new Date(inicio);
      maxFin.setFullYear(inicio.getFullYear() + MAX_YEARS);
      if (fin > maxFin) {
        fin = maxFin;
      }
      if (fin > maxAllowed) {
        fin = new Date(maxAllowed);
      }
      // Evitar reescribir inicio (el datepicker ya lo acaba de setear).
      // Diferir Fin: si se hace en el mismo tick que cambia minDate, PrimeNG deja el input en blanco.
      const finToSet = fin;
      setTimeout(() => {
        group.get('fin')?.setValue(this.toDateOnly(finToSet), { emitEvent: false });
        if (this.isHourlyView) {
          void this.applyConfig();
        }
      });
      return;
    }

    if (fin < inicio) {
      inicio = new Date(fin);
    }
    const minInicio = new Date(fin);
    minInicio.setFullYear(fin.getFullYear() - MAX_YEARS);
    if (inicio < minInicio) {
      inicio = minInicio;
    }
    if (inicio < minAllowed) {
      inicio = new Date(minAllowed);
    }
    const inicioToSet = inicio;
    setTimeout(() => {
      group.get('inicio')?.setValue(this.toDateOnly(inicioToSet), { emitEvent: false });
    });
  }

  private toDateOnly(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  onInicioYearChange(event: { year?: number; month?: number }, index: number): void {
    this.updateDateRangeWarningFromPicker(event, index);
  }

  onInicioMonthChange(event: { year?: number; month?: number }, index: number): void {
    this.updateDateRangeWarningFromPicker(event, index);
  }

  private updateDateRangeWarningFromPicker(
    event: { year?: number; month?: number },
    index: number,
  ): void {
    const fin = this.comparativas.at(index).get('fin')?.value;
    const inicioActual = this.comparativas.at(index).get('inicio')?.value;
    if (!(fin instanceof Date) || event.year == null || event.month == null) {
      this.showDateRangeYearWarning.set(false);
      return;
    }

    const day = inicioActual instanceof Date ? inicioActual.getDate() : 1;
    const lastDayOfMonth = new Date(event.year, event.month, 0).getDate();
    const provisionalInicio = new Date(event.year, event.month - 1, Math.min(day, lastDayOfMonth));

    const maxFin = new Date(provisionalInicio);
    maxFin.setFullYear(provisionalInicio.getFullYear() + 2);
    this.showDateRangeYearWarning.set(fin.getTime() > maxFin.getTime());
  }

  clearDateRangeYearWarning(): void {
    this.showDateRangeYearWarning.set(false);
  }

  async applyConfig() {
    if (this.configForm.invalid) return;

    this.first = 0;
    this.initialTableLoad = false;
    this.tableInitialized.set(false);
    await this.reloadDailyData();
  }
  resetConfig() {
    const hoy = new Date();
    const haceDosAnios = new Date();
    haceDosAnios.setFullYear(hoy.getFullYear() - 2);

    this.comparativas.at(0).patchValue({
      inicio: null,
      fin: null,
    });
    this.cdr.detectChanges();
  }

  setDateByType(type: string) {
    const endDate = this.formatDate(new Date());
    let startDate: string;
    let label: string;

    switch (type) {
      case 'DAY':
        startDate = this.getDateDaysAgo(30);
        label = 'Último mes';
        break;
      case 'WEEK':
        startDate = this.getDateMonthsAgo(1);
        label = 'Último mes';
        break;
      case 'MONTH':
        startDate = this.getDateYearsAgo(1);
        label = 'Último año';
        break;
      case 'YEAR':
        startDate = this.getDateYearsAgo(2);
        label = 'Últimos 2 años';
        break;
      default:
        return;
    }

    this.configForm.controls['predefineDateOption'].setValue({
      label,
      value: { startDate, endDate },
    });
    this.applyRangeToComparativas(startDate, endDate);
  }

  async onFormatTypeChange(): Promise<void> {
    if (!this.isHourlyView) {
      this.setDateByType(this.configForm.controls['frequencyType'].value);
    }
    this.first = 0;
    this.initialTableLoad = false;
    this.tableInitialized.set(false);
    await this.reloadDailyData();
  }

  async onFrequencyTypeChange(event: { value: string }): Promise<void> {
    if (!this.isHourlyView) {
      this.setDateByType(event.value);
    }
    this.first = 0;
    this.initialTableLoad = false;
    this.tableInitialized.set(false);
    await this.reloadDailyData();
  }

  chartDrillFor(consumptionDate: string): ChartDrill | null {
    const point = this.consumptionCalendarDate(consumptionDate);
    if (!point) {
      return null;
    }

    switch (this.configForm.controls['frequencyType'].value) {
      case 'YEAR':
        return this.frequencyDrill(
          'MONTH',
          new Date(point.getFullYear(), 0, 1),
          new Date(point.getFullYear(), 11, 31),
        );
      case 'MONTH':
        return this.frequencyDrill(
          'DAY',
          new Date(point.getFullYear(), point.getMonth(), 1),
          new Date(point.getFullYear(), point.getMonth() + 1, 0),
        );
      case 'WEEK': {
        const fin = new Date(point);
        fin.setDate(point.getDate() + 6);
        return this.frequencyDrill('DAY', point, fin);
      }
      case 'DAY': {
        if (!this.isHourlyReadingsAvailable()) {
          return null;
        }
        const range = this.clampDrillRange(point, point);
        return range ? { kind: 'hourly', day: range.inicio } : null;
      }
      default:
        return null;
    }
  }

  chartDrillHint(): string | null {
    switch (this.configForm.controls['frequencyType'].value) {
      case 'YEAR':
        return 'Pulsa para ver los meses de este año';
      case 'MONTH':
        return 'Pulsa para ver los días de este mes';
      case 'WEEK':
        return 'Pulsa para ver los días de esta semana';
      case 'DAY':
        return this.isHourlyReadingsAvailable() ? 'Pulsa para ver las horas de este día' : null;
      default:
        return null;
    }
  }

  async drillFromChartPoint(consumptionDate: string | null | undefined): Promise<void> {
    if (!consumptionDate) {
      return;
    }
    const drill = this.chartDrillFor(consumptionDate);
    if (!drill) {
      return;
    }

    this.clearDateRangeYearWarning();
    if (drill.kind === 'hourly') {
      this.configForm.controls['formatType'].setValue('1');
      this.comparativas
        .at(0)
        .patchValue({ inicio: drill.day, fin: drill.day }, { emitEvent: false });
    } else {
      this.configForm.controls['frequencyType'].setValue(drill.frequency);
      this.comparativas
        .at(0)
        .patchValue({ inicio: drill.inicio, fin: drill.fin }, { emitEvent: false });
    }

    this.first = 0;
    this.initialTableLoad = false;
    this.tableInitialized.set(false);
    await this.reloadDailyData();
  }

  private frequencyDrill(frequency: 'MONTH' | 'DAY', inicio: Date, fin: Date): ChartDrill | null {
    const range = this.clampDrillRange(inicio, fin);
    if (!range) {
      return null;
    }
    return { kind: 'frequency', frequency, inicio: range.inicio, fin: range.fin };
  }

  private clampDrillRange(inicio: Date, fin: Date): { inicio: Date; fin: Date } | null {
    const min = this.toDateOnly(this.minDate);
    const max = this.toDateOnly(this.maxDate);
    let start = this.toDateOnly(inicio);
    let end = this.toDateOnly(fin);
    if (start < min) {
      start = min;
    }
    if (end > max) {
      end = max;
    }
    if (start > end) {
      return null;
    }
    return { inicio: start, fin: end };
  }

  private consumptionCalendarDate(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return this.toDateOnly(date);
  }

  /** @deprecated Usar onFormatTypeChange / onFrequencyTypeChange */
  async onSelectFrequencyType(event: any) {
    const frequencyValues = ['DAY', 'WEEK', 'MONTH', 'YEAR'];
    if (frequencyValues.includes(event.value)) {
      await this.onFrequencyTypeChange(event);
    } else {
      await this.onFormatTypeChange();
    }
  }
  createChart() {
    const data = this.dailyConsumptions();
    if (!data.length) {
      this.createSummary([]);
      return;
    }
    queueMicrotask(() => {
      if (this.configForm.controls['formatType'].value == '0') {
        if (this.chartContainer) {
          setTimeout(() => {
            this.initConsumptionChart(data);
            this.createSummary(data);
          }, 100);
        }
      } else {
        if (this.chartContainer2) {
          setTimeout(() => {
            this.initConsumptionChart2(data);
            this.createSummary(data);
          }, 100);
        }
      }
    });
  }

  private initConsumptionChart(data: any): void {
    const chartDom = this.chartContainer.nativeElement;
    const ro = new ResizeObserver(() => {
      if (chartDom.clientWidth > 0 && chartDom.clientHeight > 0) {
        this.renderConsumptionChart(chartDom, data);
        ro.disconnect();
      }
    });

    ro.observe(chartDom);
  }

  private attachMagicTypeLineColorHandler(
    chart: echarts.ECharts,
    option: echarts.EChartsOption,
  ): void {
    const lineColor = this.getColorByType();
    chart.on('magictypechanged', (...args: unknown[]) => {
      const params = args[0] as { currentType?: string };
      if (params.currentType === 'line') {
        chart.setOption({
          series: [
            {
              itemStyle: { color: lineColor },
              lineStyle: { color: lineColor, width: 2 },
              symbol: 'circle',
              symbolSize: 8,
            },
          ],
        });
        return;
      }
      if (params.currentType === 'bar') {
        chart.setOption({ series: option.series }, { replaceMerge: ['series'] });
      }
    });
  }

  private renderConsumptionChart(dom: HTMLElement, data: any) {
    const chartData = data;
    const units = this.dailyConsumptionsUnits() || '';

    const existingChart = echarts.getInstanceByDom(dom);
    if (existingChart) existingChart.dispose();
    const myChart = echarts.init(dom);

    const categories = chartData.map((item: any) => {
      const date = new Date(item.consumptionDate);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    });
    const narrow = dom.clientWidth < 768;
    const barData = this.buildDailyBarSeriesData(chartData);
    const minValueSpan = Math.min(7, Math.max(categories.length, 1)) - 1;
    const { inicio, fin } = this.comparativas.at(0).value;
    const periodLabel = `Desde el ${this.dateSpanishFormat(inicio)} hasta el ${this.dateSpanishFormat(fin)}`;
    const option: echarts.EChartsOption = {
      aria: { enabled: true },
      title: {
        text: `Evolución de tu consumo de ${this.configForm.controls['supplyType'].value.supplyName} `,
        left: narrow ? '2%' : 'center',
        top: '2%',
        ...(narrow && {
          width: dom.clientWidth * 0.45,
          overflow: 'break',
        }),
        textStyle: {
          color: '#1d1d1f',
          fontSize: narrow ? 14 : 18,
          fontWeight: 'bold',
          fontFamily: 'SF Pro Display, sans-serif',
        },
        subtext: `${periodLabel}`,
        subtextStyle: {
          color: this.getColorByType(),
        },
      },
      toolbox: {
        show: true,
        top: narrow ? '2%' : '0%',
        right: narrow ? '0%' : '5%',
        feature: {
          saveAsImage: { title: 'Descargar', name: 'Grafico_Consumos' },
          magicType: { type: ['line', 'bar'], title: { line: 'Líneas', bar: 'Barras' } },
          restore: { title: 'Reset' },
        },
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
          start: 0,
          end: 100,
          minValueSpan,
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          filterMode: 'none',
          minValueSpan,
          height: 18,
          left: narrow ? '20%' : '10%',
          right: narrow ? '20%' : '10%',
          bottom: '5%',
          fillerColor: this.getColorByTypeTransparency(),
          handleStyle: {
            color: this.getColorByType(),
            shadowBlur: 1,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      ],
      grid: {
        top: '15%',
        bottom: '15%',
        left: '5%',
        right: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 12,
        borderWidth: 0,
        padding: 12,
        shadowBlur: 10,
        shadowColor: 'rgba(0,0,0,0.1)',
        textStyle: { color: '#1d1d1f', fontFamily: 'SF Pro Display, sans-serif' },
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const dataItem = chartData[p.dataIndex];
          const dynamicColor = this.getColorByType();
          const estimated = this.isEstimatedReadingType(dataItem?.readingType);
          const estimatedLabel = estimated ? ' · Estimado' : '';
          const hint = this.chartDrillHint();
          const hintHtml = hint
            ? `<div style="font-size:11px; color:#134b70; margin-top:4px">${hint}</div>`
            : '';
          return `
          <div style="font-weight:600; color:${dynamicColor}">${this.spanishFormatConsumption(dataItem.consumptionValue)} ${units}${estimatedLabel}</div>
          <div style="font-size:11px; color:#86868b">${this.dateSpanishFormat(dataItem.consumptionDate)}</div>
          ${hintHtml}
        `;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#86868b',
          margin: 15,
          formatter: (value: string) => `${this.formatByFrequencyGP(value)}`,
        },
      },
      yAxis: {
        scale: true,
        type: 'value',
        splitLine: { lineStyle: { color: '#f5f5f7' } },
        axisLabel: {
          color: '#86868b',
          formatter: (value: number) => `${this.spanishFormatConsumption(value)} ${units}`,
        },
      },
      series: [
        {
          data: barData,
          type: 'bar',
          cursor: this.chartDrillHint() ? 'pointer' : 'default',
          barWidth: '40%',
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(0, 0, 0, 0.02)',
            borderRadius: [6, 6, 0, 0],
          },
        },
      ],
    };

    myChart.setOption(option);
    myChart.on('click', (params) => {
      const click = params as { componentType?: string; dataIndex?: number };
      if (click.componentType !== 'series' || click.dataIndex == null) {
        return;
      }
      const item = chartData[click.dataIndex] as { consumptionDate?: string } | undefined;
      void this.drillFromChartPoint(item?.consumptionDate);
    });
    this.attachMagicTypeLineColorHandler(myChart, option);
    window.addEventListener('resize', () => myChart.resize());
  }
  private initConsumptionChart2(data: any): void {
    const chartDom = this.chartContainer2.nativeElement;

    const ro = new ResizeObserver(() => {
      if (chartDom.clientWidth > 0 && chartDom.clientHeight > 0) {
        this.renderConsumptionChart2(chartDom, data);

        ro.disconnect();
      }
    });

    ro.observe(chartDom);
  }

  private renderConsumptionChart2(dom: HTMLElement, data: any) {
    const chartData = data;
    const units = this.dailyConsumptionsUnits() || '';
    const existingChart = echarts.getInstanceByDom(dom);
    if (existingChart) existingChart.dispose();
    const myChart = echarts.init(dom);

    const categories = chartData.map((item: { consumptionDate: string }) =>
      this.formatHourFromConsumptionDate(item.consumptionDate),
    );
    const narrow = dom.clientWidth < 768;
    const barData = this.buildDailyBarSeriesData(chartData);
    const minValueSpan = Math.min(7, Math.max(categories.length, 1)) - 1;
    const { inicio } = this.comparativas.at(0).value;
    const periodLabel = `Día seleccionado: ${this.dateSpanishFormat(inicio)}`;
    const option: echarts.EChartsOption = {
      aria: { enabled: true },
      title: {
        text: `Evolución de tu consumo de ${this.configForm.controls['supplyType'].value.supplyName} `,
        left: narrow ? '5%' : 'center',
        top: '2%',
        ...(narrow && {
          width: dom.clientWidth * 0.45,
          overflow: 'break',
        }),
        textStyle: {
          color: '#1d1d1f',
          fontSize: narrow ? 14 : 18,
          fontWeight: 'bold',
          fontFamily: 'SF Pro Display, sans-serif',
        },
        subtext: `${periodLabel}`,
        subtextStyle: {
          color: this.getColorByType(),
        },
      },
      toolbox: {
        show: true,
        top: narrow ? '2%' : '0%',
        right: narrow ? '0%' : '5%',
        feature: {
          saveAsImage: { title: 'Descargar', name: 'Grafico_Consumos' },
          magicType: { type: ['line', 'bar'], title: { line: 'Líneas', bar: 'Barras' } },
          restore: { title: 'Reset' },
        },
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
          start: 0,
          end: 100,
          minValueSpan,
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          filterMode: 'none',
          minValueSpan,
          height: 18,
          left: narrow ? '20%' : '10%',
          right: narrow ? '20%' : '10%',
          bottom: '5%',

          fillerColor: this.getColorByTypeTransparency(),
          /*       handleIcon: 'circle', brushSelect: false, */
          textStyle: {
            fontSize: 12,
            fontWeight: 'bold',
          },
          handleStyle: {
            color: this.getColorByType(),
            shadowBlur: 1,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      ],
      grid: {
        top: '15%',
        bottom: '15%',
        left: '5%',
        right: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 12,
        borderWidth: 0,
        padding: 12,
        shadowBlur: 10,
        shadowColor: 'rgba(0,0,0,0.1)',
        textStyle: { color: '#1d1d1f', fontFamily: 'SF Pro Display, sans-serif' },
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const dataItem = chartData[p.dataIndex];
          const dynamicColor = this.getColorByType();
          const timeLabel = this.isHourlyView
            ? this.formatHourFromConsumptionDate(dataItem.consumptionDate)
            : this.dateSpanishFormat(dataItem.consumptionDate);
          const estimated = this.isEstimatedReadingType(dataItem?.readingType);
          const estimatedLabel = estimated ? ' · Estimado' : '';
          return `
          <div style="font-weight:600; color:${dynamicColor}">${this.spanishFormatConsumption(dataItem.consumptionValue)} ${units}${estimatedLabel}</div>
          <div style="font-size:11px; color:#86868b"> ${timeLabel}</div>

        `;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#86868b', margin: 15 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f5f5f7' } },
        axisLabel: {
          color: '#86868b',
          formatter: (value: number) => `${this.spanishFormatConsumption(value)} ${units}`,
        },
      },
      series: [
        {
          data: barData,
          type: 'bar',
          barWidth: '40%',
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(0, 0, 0, 0.02)',
            borderRadius: [6, 6, 0, 0],
          },
        },
      ],
    };

    myChart.setOption(option);
    this.attachMagicTypeLineColorHandler(myChart, option);
    window.addEventListener('resize', () => myChart.resize());
  }

  createSummary(data: any[]) {
    if (!data || data.length === 0) {
      this.resumen = {
        avg: '0,00',
        maxV: '0,00',
        maxLabel: '---',
        minV: '0,00',
        minLabel: '---',
        total: '0,00',
        units: this.dailyConsumptionsUnits() || '',
      };
      this.cdr.detectChanges();
      return;
    }

    let total = 0;
    let maxItem = data[0];
    let minItem = data[0];
    data.forEach((item: any) => {
      const val = item.consumptionValue || 0;
      total += val;
      if (val > (maxItem.consumptionValue || 0)) maxItem = item;
      if (val < (minItem.consumptionValue || 0)) minItem = item;
    });
    const avg = total / data.length;
    const formatSummaryLabel = (consumptionDate: string): string =>
      this.isHourlyView
        ? this.formatHourFromConsumptionDate(consumptionDate)
        : this.formatByFrequency(this.dateSpanishFormat(consumptionDate));

    this.resumen = {
      avg: this.spanishFormatConsumption(avg),
      total: this.spanishFormatConsumption(total),
      maxV: this.spanishFormatConsumption(maxItem.consumptionValue),
      maxLabel: formatSummaryLabel(maxItem.consumptionDate),
      minLabel: formatSummaryLabel(minItem.consumptionDate),
      minV: this.spanishFormatConsumption(minItem.consumptionValue),
      units: this.dailyConsumptionsUnits() || '',
    };
    this.cdr.detectChanges();
  }

  dateSpanishFormat(dateTime: string | Date | null | undefined): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  formatHourFromConsumptionDate(dateTime: string | null | undefined): string {
    if (!dateTime) {
      return '';
    }

    const trimmed = String(dateTime).trim();
    const timeOnlyMatch = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
    if (timeOnlyMatch) {
      return `${timeOnlyMatch[1].padStart(2, '0')}:${timeOnlyMatch[2]}`;
    }

    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) {
      return trimmed;
    }

    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  spanishFormat(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '0,00';
    const numberValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numberValue);
  }

  spanishFormatConsumption(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '0,000';
    const numberValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(numberValue);
  }

  exportDisabled(): boolean {
    if (!this.tableInitialized() || this.loading || this.isDownloading()) {
      return true;
    }

    return this.dailyConsumptions().length === 0;
  }

  async exportReadings(format: ExportFormat): Promise<void> {
    if (this.exportDisabled()) {
      return;
    }

    this.isDownloading.set(true);
    try {
      const table = this.buildReadingsExport();
      if (table.rows.length === 0) {
        return;
      }
      await downloadTableFile(table, format);
    } catch (error) {
      console.error('Error al exportar lecturas:', error);
    } finally {
      this.isDownloading.set(false);
    }
  }

  private buildReadingsExport(): ExportTable {
    const source = this.dailyConsumptions();
    const units = this.dailyConsumptionsUnits();
    const hourly = this.isHourlyView;

    return {
      filename: exportFilename(hourly ? 'lecturas-horarias' : 'lecturas-diarias'),
      headers: [
        hourly ? 'Hora' : 'Fecha',
        this.exportColumnLabel('Lectura', units),
        this.exportColumnLabel('Consumo', units),
        'Tipo lectura',
      ],
      rows: [...source]
        .sort((left, right) =>
          this.compareConsumptionDateDesc(left.consumptionDate, right.consumptionDate),
        )
        .map((item) => [
          exportTextCell(
            hourly
              ? this.formatHourFromConsumptionDate(item.consumptionDate)
              : this.dateSpanishFormat(item.consumptionDate),
          ),
          exportNumberCell(
            item.readingValue,
            this.formatMeteringTableAmount(item.readingValue, false, this.readingAmountDecimals()),
          ),
          exportNumberCell(
            item.consumptionValue,
            this.spanishFormatConsumption(item.consumptionValue),
          ),
          exportTextCell(this.formatReadingType(item.readingType)),
        ]),
    };
  }

  private exportColumnLabel(prefix: string, units: string): string {
    const trimmed = units.trim();
    return trimmed ? `${prefix} ${trimmed}` : prefix;
  }

  private compareConsumptionDateDesc(left: string, right: string): number {
    const leftTime = new Date(left).getTime();
    const rightTime = new Date(right).getTime();
    if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
      return right.localeCompare(left);
    }
    return rightTime - leftTime;
  }

  isEstimatedReadingType(readingType: string | null | undefined): boolean {
    return readingType?.toUpperCase() === 'ESTIMACION';
  }

  isEmptyConsumptionRow(consumption: {
    readingType?: string | null;
    readingValue?: number | null;
    consumptionValue?: number | null;
  }): boolean {
    if (this.isEstimatedReadingType(consumption.readingType)) {
      return false;
    }
    const noReading = consumption.readingValue == null;
    const noConsumption =
      consumption.consumptionValue == null || consumption.consumptionValue === 0;
    return noReading && noConsumption;
  }

  readingAmountDecimals(): 2 | 3 {
    return this.isCubicMeterUnit(this.dailyConsumptionsUnits()) ? 3 : 2;
  }

  formatMeteringTableAmount(
    value: number | string | null | undefined,
    empty: boolean,
    decimals: 2 | 3 = 2,
  ): string {
    if (empty) {
      return '—';
    }
    return decimals === 3 ? this.spanishFormatConsumption(value) : this.spanishFormat(value);
  }

  private isCubicMeterUnit(units: string | null | undefined): boolean {
    const normalized = (units ?? '').trim().toLowerCase().replace(/³/g, '3').replace(/\^/g, '');
    return normalized === 'm3';
  }

  private dailyBarColor(chartData: { readingType?: string }[], dataIndex: number): string {
    if (this.isEstimatedReadingType(chartData[dataIndex]?.readingType)) {
      return this.getEstimatedBarColor();
    }
    return this.getColorByType();
  }

  private dailyBarDecal(chartData: { readingType?: string }[], dataIndex: number) {
    if (!this.isEstimatedReadingType(chartData[dataIndex]?.readingType)) {
      return null;
    }
    return {
      symbol: 'rect',
      symbolSize: 4,
      dashArrayX: [1, 0],
      dashArrayY: [4, 3],
      rotation: -Math.PI / 4,
      color: 'rgba(255, 255, 255, 0.45)',
    };
  }

  private buildDailyBarSeriesData(
    chartData: { consumptionValue?: number; readingType?: string }[],
  ) {
    const borderRadius: [number, number, number, number] = [6, 6, 0, 0];
    return chartData.map((item, dataIndex) => {
      const color = this.dailyBarColor(chartData, dataIndex);
      const decal = this.dailyBarDecal(chartData, dataIndex);
      return {
        value: item.consumptionValue ?? 0,
        itemStyle: decal ? { color, borderRadius, decal } : { color, borderRadius },
      };
    });
  }

  formatReadingType(value: string | null | undefined): string {
    if (!value) return '—';
    const labels: Record<string, string> = {
      TELELECTURA: 'Telelectura',
      FACILITADA: 'Facilitada',
      REAL: 'Real',
      ESTIMADA: 'Estimada',
      ESTIMACION: 'Estimación',
    };
    const normalized = labels[value.toUpperCase()];
    if (normalized) return normalized;
    const lower = value.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  createDateGroup(): FormGroup {
    const hoy = new Date();
    const haceUnMes = new Date();
    haceUnMes.setMonth(hoy.getMonth() - 1);
    return this.fb.group({
      inicio: [haceUnMes, Validators.required],
      fin: [hoy, Validators.required],
    });
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatByFrequency(dateStr: string): string {
    const parts = dateStr.split('/');
    if (parts.length < 3) return dateStr;
    const month = parts[1];
    const year = parts[2];
    return `${month}/${year}`;
  }
  addComparativa() {
    this.comparativas.push(this.createDateGroup());
  }

  removeComparativa(index: number) {
    this.comparativas.removeAt(index);
  }
  async onMeterSliderChange(event: any) {
    const newIndex = event.page;
    this.currentPage.set(newIndex);

    const selectedMeter = this.filteredMeters()[newIndex];
    if (!selectedMeter) return;
    this.configForm.get('meter')?.setValue(selectedMeter, { emitEvent: false });

    const formatType = this.configForm.get('formatType')?.value;
    const supplyTypeId = this.configForm.get('supplyType')?.value?.supplyTypeId;

    const shouldLoadConsumption = supplyTypeId !== 14 || formatType === 'Consumo individual';

    if (shouldLoadConsumption) {
      await this.getConsumptionsDaily();
    }
  }
  getColorByType(): string {
    const typeId = this.configForm.controls['supplyType'].value;

    switch (typeId.supplyTypeId) {
      case 14: // repartidores
        return '#FFAB40'; // orangeAccent
      case 1: // agua fria
        return '#448AFF'; // blueAccent
      case 2: // Agua caliente
        return '#F44336'; // red
      case 3: // Calefacción
        return '#FFAB40'; // orangeAccent (radiadores)
      case 4: // Aire acondicionado
        return '#00BCD4'; // cyan
      default:
        return '#448AFF'; // blueAccent por defecto
    }
  }
  private lightenWithAlpha(hex: string, alpha = 0.6, towardWhite = 0.2): string {
    const clean = hex.replace('#', '');

    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);

    const lighten = (c: number) => Math.min(255, Math.floor(c + (255 - c) * towardWhite));

    const lr = lighten(r);
    const lg = lighten(g);
    const lb = lighten(b);

    return `rgba(${lr}, ${lg}, ${lb}, ${alpha})`;
  }

  getEstimatedBarColor(): string {
    return this.lightenWithAlpha(this.getColorByType(), 0.9, 0.35);
  }
  getColorByTypeTransparency(): string {
    const typeId = this.configForm.controls['supplyType'].value;
    let baseColor = '';

    switch (typeId.supplyTypeId) {
      case 14: // repartidores
        baseColor = '#FFAB40';
        break;

      case 1: // agua fria
        baseColor = '#448AFF';
        break;

      case 2: // Agua caliente
        baseColor = '#F44336';
        break;

      case 3: // Calefacción
        baseColor = '#FFAB40';
        break;

      case 4: // Aire acondicionado
        baseColor = '#00BCD4';
        break;

      default:
        baseColor = '#448AFF';
    }

    return this.lightenWithAlpha(baseColor, 0.6);
  }
  getDateDaysAgo(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return this.formatDate(date);
  }

  getDateMonthsAgo(months: number): string {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    return this.formatDate(date);
  }

  getDateYearsAgo(years: number): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - years);
    return this.formatDate(date);
  }

  private ensureHourlyViewAllowed(): boolean {
    if (!this.isHourlyView || this.isHourlyReadingsAvailable()) {
      return false;
    }
    this.configForm.controls['formatType'].setValue('0', { emitEvent: false });
    this.setDateByType(this.configForm.controls['frequencyType'].value);
    return true;
  }

  private collapseRangeToSelectedDay(): void {
    const fin = this.comparativas.at(0).get('fin')?.value;
    let day = fin instanceof Date ? this.toDateOnly(fin) : this.toDateOnly(new Date());
    const max = this.toDateOnly(this.maxDate);
    const min = this.toDateOnly(this.minDate);
    if (day > max) {
      day = max;
    }
    if (day < min) {
      day = min;
    }
    this.comparativas.at(0).patchValue({ inicio: day, fin: day }, { emitEvent: false });
  }

  private applyRangeToComparativas(startDate: string, endDate: string): void {
    const inicio = this.parseDateString(startDate);
    const fin = this.parseDateString(endDate);
    if (!inicio || !fin) {
      return;
    }
    this.comparativas.at(0).patchValue({ inicio, fin }, { emitEvent: false });
  }

  private parseDateString(value: string): Date | null {
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
      return null;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  getDateWeeksAgo(weeks: number): string {
    const date = new Date();
    date.setDate(date.getDate() - weeks * 7);
    return this.formatDate(date);
  }
  formatByFrequencyGP(preSecondFormatedDate: any): string {
    const parts = preSecondFormatedDate?.toString().split('/');

    if (!parts || parts.length < 3) {
      return preSecondFormatedDate?.toString() || '';
    }

    const date = new Date(
      Number(parts[2]), // Año
      Number(parts[1]) - 1, // Mes (JS empieza en 0)
      Number(parts[0]), // Día
    );

    const pad = (n: number): string => n.toString().padStart(2, '0');
    switch (this.configForm.controls['frequencyType'].value) {
      case 'DAY':
        return preSecondFormatedDate.toString();
      case 'WEEK':
        const endDate = new Date(date);
        endDate.setDate(date.getDate() + 6);

        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} - ${pad(endDate.getDate())}/${pad(endDate.getMonth() + 1)}`;

      case 'MONTH':
        return `${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

      case 'YEAR':
        return `${date.getFullYear()}`;

      default:
        return preSecondFormatedDate.toString();
    }
  }

  ngOnDestroy(): void {
    if (this.narrowViewportQuery && this.narrowViewportListener) {
      this.narrowViewportQuery.removeEventListener('change', this.narrowViewportListener);
    }
  }

  useFixedDailyConsumptionsTableRowHeight(): boolean {
    const total = this.visibleDailyConsumptionsTable().length;
    if (total <= 0) {
      return false;
    }
    if (this.isHourlyView) {
      return total < 8;
    }
    const pageLength = Math.min(this.rows, Math.max(total - this.first, 0));
    return pageLength > 0 && pageLength < 8;
  }

  get consumptionsTableScrollable(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsTableScrollHeight(): string | undefined {
    if (this.isNarrowViewport() || this.useFixedDailyConsumptionsTableRowHeight()) {
      return undefined;
    }
    return 'calc(-300px + 95vh)';
  }

  get consumptionsTableStyle(): Record<string, string> {
    if (this.isNarrowViewport()) {
      return { width: '100%' };
    }
    if (this.useFixedDailyConsumptionsTableRowHeight()) {
      return { 'min-width': '50rem' };
    }
    return { 'min-width': '50rem', 'min-height': 'calc(-400px + 95vh)' };
  }

  get consumptionsTableStyleClass(): string | undefined {
    const classes: string[] = [];
    if (this.isNarrowViewport()) {
      classes.push('daily-readings-consumptions-table-narrow');
    }
    if (this.useFixedDailyConsumptionsTableRowHeight()) {
      classes.push('daily-readings-table-rows-fixed');
    }
    return classes.length ? classes.join(' ') : undefined;
  }

  get consumptionsTableShowCurrentPageReport(): boolean {
    return true;
  }

  get consumptionsTableShowPageLinks(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsTableShowFirstLastIcon(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsTableRowsPerPageOptions(): number[] | undefined {
    return this.isNarrowViewport() ? undefined : [10, 20, 50, 100];
  }

  get consumptionsTablePaginatorStyleClass(): string | undefined {
    if (!this.isNarrowViewport()) {
      return undefined;
    }
    return 'w-100 d-flex flex-wrap justify-content-center align-items-center gap-2 py-2';
  }

  private initNarrowViewportListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.narrowViewportQuery = window.matchMedia('(max-width: 767px)');
    this.isNarrowViewport.set(this.narrowViewportQuery.matches);
    this.narrowViewportListener = (event: MediaQueryListEvent) => {
      this.isNarrowViewport.set(event.matches);
    };
    this.narrowViewportQuery.addEventListener('change', this.narrowViewportListener);
  }
}
